// src/components/molecules/FormFields/VideoUpload.js

import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Grid,
  Button,
  LinearProgress,
  CircularProgress,
  alpha,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useTheme } from "styled-components"; // Retained import from styled-components
import {
  createSubmissionAndGenerateSasToken,
  uploadFileToAzure,
  uploadFileToGCPBucket,
  updateArtwork,
  BLOB_URI,
  CONTAINER_NAME,
} from "../../../utils/api"; // Import the API functions

const UploadArtworkMedia = ({
  onUpload,
  onUploadingChange,
  draftData,
  onFileSelectionChange,
  required = false,
}) => {
  const theme = useTheme();
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [submissionData, setSubmissionData] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [videoUploaded, setVideoUploaded] = useState(false); // Track if the upload is successful
  const [isGeneratingSas, setIsGeneratingSas] = useState(false); // Track if SAS token is being generated
  const [previewUrl, setPreviewUrl] = useState(null);

  // Handle file selection
  const handleFileSelection = async (event) => {
    // Get the selected file
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setSelectedFileName(file.name);
      setVideoUploaded(false); // Reset videoUploaded when a new file is selected
      onFileSelectionChange(file); // Notify the parent component about the file selection
      const fileUrl = URL.createObjectURL(file);
      setPreviewUrl(fileUrl);
    }
  };

  const handleCreateSubmissionAndGenerateSasToken = async () => {
    try {
      setIsGeneratingSas(true);
      const { submissionSaleId, saleId, sasToken } =
        await createSubmissionAndGenerateSasToken();

      // Store the submission data in state
      setSubmissionData({ submissionSaleId, saleId, sasToken });

      return { submissionSaleId, saleId, sasToken };
    } catch (error) {
      console.error("Error generating submission data:", error);
      throw error;
    } finally {
      setIsGeneratingSas(false);
    }
  };

  // Handle the actual file upload when the user clicks "Upload Your Video"
  const handleFileUploadClick = async () => {
    try {
      if (!selectedFile) {
        throw new Error("No file selected");
      }

      let foundSubmissionSaleId, foundSaleId, foundSasToken;

      // Check if submissionData is available then use it, otherwise generate new data
      if (
        !submissionData ||
        !submissionData.submissionSaleId ||
        !submissionData.saleId ||
        !submissionData.sasToken
      ) {
        const { submissionSaleId, saleId, sasToken } =
          await handleCreateSubmissionAndGenerateSasToken();

        foundSubmissionSaleId = submissionSaleId;
        foundSaleId = saleId;
        foundSasToken = sasToken;
      } else {
        foundSubmissionSaleId = submissionData.submissionSaleId;
        foundSaleId = submissionData.saleId;
        foundSasToken = submissionData.sasToken;
      }

      // Start the upload process
      setIsUploading(true);
      if (onUploadingChange) {
        onUploadingChange(true); // Notify parent that upload has started
      }
      setUploadProgress(0);

      // Upload the file to Azure with progress tracking
      const uploadBlobResponse = await uploadFileToGCPBucket(
        selectedFile,
        foundSubmissionSaleId,
        (progress) => {
          // console.log("Progress:", progress); // Debugging log

          // Calculate the percentage based on loadedBytes and totalBytes
          const loaded = progress.loadedBytes || progress.loaded || 0;
          const total =
            progress.totalBytes || progress.total || selectedFile.size;
          const percentage = total > 0 ? Math.round((loaded / total) * 100) : 0;
          setUploadProgress(percentage);
        }
      );

      // console.log("Upload Blob Response:", uploadBlobResponse);
      const StorageUrl = process.env.REACT_APP_GCP_STORAGE_URL;
      const BucketName = process.env.REACT_APP_GCP_STORAGE_BUCKET;
      // Prepare to update artwork metadataa
      const fileUrl = `${StorageUrl}/public/${foundSubmissionSaleId}`;
      const contentMD5 = uploadBlobResponse.contentMD5;

      // Call updateArtwork to update the metadata
      const updateResponse = await updateArtwork(
        foundSubmissionSaleId,
        fileUrl,
        selectedFile.name,
        contentMD5
      );

      console.log("Artwork Update Response:", updateResponse);

      // Set upload success state
      setVideoUploaded(true);

      // Notify the parent component that the upload was successful
      if (onUpload) {
        onUpload(true, {
          saleId: foundSaleId,
          submissionSaleId: foundSubmissionSaleId,
          sasToken: foundSasToken,
          fileName: selectedFile.name,
          contentType: selectedFile.type,
        });
      }
    } catch (error) {
      console.error("Error uploading file or updating artwork:", error);
      if (onUpload) {
        onUpload(false); // Notify the parent component that the upload failed
      }
    } finally {
      setIsUploading(false);
      if (onUploadingChange) {
        onUploadingChange(false); // Notify parent that upload has finished
      }
      setUploadProgress(0);
      setSelectedFile(null);
      setSelectedFileName("");
      setSubmissionData(null); // Clear submission data after upload
    }
  };

  useEffect(() => {
    if (
      draftData?.submissionSaleId &&
      draftData?.saleId &&
      draftData?.sasToken
    ) {
      setSubmissionData({
        submissionSaleId: draftData.submissionSaleId,
        saleId: draftData.saleId,
        sasToken: draftData.sasToken,
      });
    }
  }, [draftData]);

  // useEffect(() => {
  //   // Notify parent about the selected file
  //   if (onFileSelected && selectedFile && submissionData) {
  //     onFileSelected({
  //       submissionSaleId: submissionData.submissionSaleId,
  //       saleId: submissionData.saleId,
  //       sasToken: submissionData.sasToken,
  //       fileName: selectedFile.name,
  //       contentType: selectedFile.type,
  //     });
  //   }
  // }, [submissionData, selectedFile, onFileSelected]);

  return (
    <Grid item xs={12}>
      {!videoUploaded ? ( // Conditional rendering based on upload success
        <>
          <Typography
            variant="body1"
            sx={{ fontFamily: "basic-sans", color: "white" }}
          >
            <span>Upload your artwork </span>{" "}
            {required && <span style={{ color: theme.colors.red }}>*</span>}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "basic-sans",
              color: theme.colors.darkGray, // Retained custom theme color
              mb: 2,
              fontSize: "0.9rem",
            }}
          >
            mp4, mov, jpeg, png, gif, and tiff accepted
          </Typography>

          <div style={{ position: "relative" }}>
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                // opacity: previewUrl ? 0.2 : 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "180px",
                padding: "20px",
                backgroundColor: previewUrl
                  ? alpha(theme.colors.green, 0.2)
                  : theme.colors.green,
                borderRadius: "10px",
                cursor: "pointer",
                transition: "background-color 0.3s ease",
                "&:hover": {
                  backgroundColor: previewUrl
                    ? alpha(theme.colors.blue, 0.2)
                    : theme.colors.blue,
                },
              }}
              onClick={() => document.getElementById("fileInput").click()} // Trigger click on hidden input
            >
              {isUploading ? (
                <CircularProgress size={48} sx={{ color: "white" }} />
              ) : (
                <AddCircleOutlineIcon
                  sx={{ fontSize: "48px", color: "white" }}
                />
              )}

              <input
                type="file"
                id="fileInput"
                onChange={handleFileSelection}
                accept="video/mp4, video/quicktime, image/jpeg, image/png, image/gif, image/tiff"
                style={{ display: "none" }}
                multiple={false}
              />

              {selectedFileName && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                    mt: 2,
                    fontFamily: "basic-sans",
                    textAlign: "center",
                  }}
                >
                  {selectedFileName}{" "}
                  {isUploading &&
                    "is uploading. This may take a moment. Please don't refresh or close the page."}
                </Typography>
              )}
            </Box>

            {previewUrl && selectedFile && (
              <div className="mt-2">
                {selectedFile.type.startsWith("image/") ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{
                      width: "100%",
                      objectFit: "contain",
                      borderRadius: "10px",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      height: "180px",
                    }}
                  />
                ) : selectedFile.type.startsWith("video/") ? (
                  <video
                    src={previewUrl}
                    controls
                    style={{
                      width: "100%",
                      borderRadius: "10px",
                      position: "absolute",

                      top: 0,
                      left: 0,
                      height: "180px",
                    }}
                    muted
                  />
                ) : null}
              </div>
            )}
          </div>

          {selectedFileName && !isUploading && !isGeneratingSas && (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleFileUploadClick} // Trigger the upload when clicked
              sx={{
                backgroundColor: theme.colors.blue, // Retained custom theme color
                color: "white",
                fontFamily: "basic-sans",
                fontWeight: "bold",
                borderRadius: "10px",
                textTransform: "none",
                padding: "10px 30px",
                marginTop: "20px",
                "&:hover": { backgroundColor: theme.colors.green }, // Retained custom theme color
              }}
            >
              Upload Your Artwork
            </Button>
          )}

          {isGeneratingSas && (
            <Typography
              variant="body2"
              sx={{
                color: "white",
                mt: 2,
                fontFamily: "basic-sans",
                textAlign: "center",
              }}
            >
              Preparing upload...
            </Typography>
          )}

          {isUploading && (
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{
                width: "100%",
                height: "10px", // Increase thickness
                borderRadius: "5px", // Round the edges
                marginTop: "20px",
                backgroundColor: theme.colors.darkBlue, // Retained custom theme color
                "& .MuiLinearProgress-bar": {
                  borderRadius: "5px", // Round the edges of the progress indicator
                  backgroundColor: theme.colors.blue, // Retained custom theme color
                },
              }}
            />
          )}
        </>
      ) : (
        <Typography
          variant="body1"
          sx={{
            fontFamily: "basic-sans",
            color: "white",
            mt: 2,
            textAlign: "center",
          }}
        >
          {selectedFileName} successfully uploaded
        </Typography>
      )}
    </Grid>
  );
};

export default UploadArtworkMedia;
